import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Link as TanStackLink } from "@tanstack/react-router";
import { Empty, Tabs, TabsProps } from "antd5";
import { supplierBuyerRoute } from "router/routes/suppliers";
import { Text } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import { DetailsPage } from "components/app_layout/DetailsLayout";
import { FilterableNoticeTable } from "components/notices/FilterableNoticeTable";
import { EMPTY_FILTERS, NoticeFilters } from "components/notices/utils";
import {
  BuyerSupplierChart,
  useBuyerSupplierChartRows,
} from "components/spend_data/BuyerSupplierChart";
import {
  BuyerSupplierHeadlineFigures,
  useHeadlineFiguresLoading,
} from "components/spend_data/BuyerSupplierHeadlineFigures";
import { BuyerSupplierOverviewSkeleton } from "components/spend_data/BuyerSupplierOverviewSkeleton";
import { BuyerSupplierTransactions } from "components/spend_data/BuyerSupplierTransactions";
import {
  BuyerSupplierRange,
  BuyerSupplierView,
  BuyerSupplierViewControls,
  rangeStart,
  useBuyerSupplierView,
} from "components/spend_data/BuyerSupplierViewControls";
import { useFittedRange } from "components/spend_data/hooks/useFittedRange";
import { useSupplierFromRoute } from "components/supplier_details/SupplierDetailLayout";
import { BreadcrumbContainer, RouterBreadcrumb } from "components/ui/breadcrumb/SimpleBreadcrumb";
import { appRoutes } from "lib/appRoutes";
import RedactedWrapper, { RedactedLink } from "lib/core_components/RedactedWrapper";
import { useOrganisation } from "lib/hooks/api/organisations/useOrganisations";
import { useOrganisationsByIds } from "lib/hooks/api/organisations/useOrganisationsByIds";
import { SpendFilters, supplierSpendFilter } from "lib/hooks/api/spend/spendFilters";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useSubscription } from "lib/hooks/subscription";
import { usePageView } from "lib/tracking";
import { LoggedEntityType, SearchOrgRole } from "lib/types/graphQLEnums";
import { BuyerDetails } from "lib/types/models";

import css from "./SupplierBuyerRelationshipPage.module.scss";

type BuyerSupplierRelationshipPageProps = {
  buyer: BuyerDetails;
  supplierGuid: string;
};

/** The relationship page reached from a buyer: the buyer comes from the route, the supplier by guid. */
export function BuyerSupplierRelationshipPage({
  buyer,
  supplierGuid,
}: BuyerSupplierRelationshipPageProps) {
  // Not a search: this route is reached with a stored guid, which may point at a merged-away
  // supplier.
  const supplier = useOrganisationsByIds([supplierGuid]);
  const supplierName = supplier.data?.organisations[0]?.name;
  // The header redacts the supplier for anyone without supplier data, so the chart does too.
  const { hasDataTypes } = useSubscription();
  const header = (
    <RedactedWrapper
      requiredDataType="SUPPLIERS"
      redactContent={
        <>
          Activity between{" "}
          <TanStackLink to="/buyers/$id" params={{ id: buyer.guid }}>
            {buyer.name}
          </TanStackLink>{" "}
          and <RedactedLink textToRedact={supplierName ?? ""} />
        </>
      }
    >
      Activity between{" "}
      <TanStackLink to="/buyers/$id" params={{ id: buyer.guid }}>
        {buyer.name}
      </TanStackLink>{" "}
      and{" "}
      <RouterTextLink {...appRoutes.supplierDetail(supplierGuid)}>{supplierName}</RouterTextLink>
    </RedactedWrapper>
  );

  return (
    <BuyerSupplierRelationshipContent
      buyerGuid={buyer.guid}
      supplierGuid={supplierGuid}
      buyerName={buyer.name}
      supplierName={hasDataTypes("SUPPLIERS") ? supplierName : undefined}
      breadcrumbOverrides={{ [`/buyers/${buyer.guid}`]: { label: buyer.name } }}
      header={header}
    />
  );
}

type SupplierBuyerRelationshipPageProps = {
  supplierName: string;
  supplierGuid: string;
  buyerGuid: string;
  supplierProfilePath: string;
};

/** The relationship page reached from a supplier: the supplier comes from the route, the buyer by guid. */
function SupplierBuyerRelationshipPage({
  buyerGuid,
  supplierName,
  supplierGuid,
  supplierProfilePath,
}: SupplierBuyerRelationshipPageProps) {
  const { isLoading, data: buyer } = useOrganisation({
    ids: [buyerGuid],
    role: SearchOrgRole.Buyer,
  });

  if (isLoading) {
    return (
      <RelationshipPlaceholder
        breadcrumbOverrides={{ [supplierProfilePath]: { label: supplierName } }}
      />
    );
  }

  if (!buyer) {
    return (
      <DetailsPage>
        <div className={css.content}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      </DetailsPage>
    );
  }

  const header = (
    <>
      Activity between <RouterTextLink to={supplierProfilePath}>{supplierName}</RouterTextLink> and{" "}
      <RouterTextLink to="/buyers/$id" params={{ id: String(buyer.id) }}>
        {buyer.name}
      </RouterTextLink>
    </>
  );

  return (
    <BuyerSupplierRelationshipContent
      buyerGuid={buyerGuid}
      buyerName={buyer.name}
      supplierName={supplierName}
      breadcrumbOverrides={{ [supplierProfilePath]: { label: supplierName } }}
      header={header}
      supplierGuid={supplierGuid}
    />
  );
}

/** Route wrapper for /suppliers/$id/buyers/$buyerGuid; reads supplier from the parent layout. */
export function SupplierBuyerRelationshipPageRoute() {
  const { buyerGuid } = supplierBuyerRoute.useParams();
  const supplier = useSupplierFromRoute();
  useLogEntityViewOnOpen(LoggedEntityType.Supplier, supplier?.guid);

  if (!supplier) return null;

  return (
    <SupplierBuyerRelationshipPage
      supplierName={supplier.name}
      supplierGuid={supplier.guid}
      buyerGuid={buyerGuid}
      supplierProfilePath={`/suppliers/${supplier.guid}`}
    />
  );
}

type BuyerSupplierRelationshipContentProps = {
  buyerGuid: string;
  supplierGuid: string;
  buyerName?: string;
  supplierName?: string;
  breadcrumbOverrides: Record<string, { label: string }>;
  header: React.ReactNode;
};

function BuyerSupplierRelationshipContent({
  buyerGuid,
  supplierGuid,
  buyerName,
  supplierName,
  breadcrumbOverrides,
  header,
}: BuyerSupplierRelationshipContentProps) {
  const pageParams = useMemo(() => ({ buyerGuid, supplierGuid }), [buyerGuid, supplierGuid]);
  usePageView("BuyerSupplierRelationshipPage", pageParams);

  // Spend is gated on BUYERS|SUPPLIERS, the same entitlement the spend dashboard
  // and its nav item use. Until the API started enforcing it this page showed
  // spend to everyone, which is what made it the one surface the check breaks.
  const { hasDataTypes } = useSubscription();
  const hasSpendAccess = hasDataTypes("SUPPLIERS");

  // One view for the whole page, set by the controls at the top, so the figures, chart and tables
  // can't disagree about which notices, or which period, they cover.
  const [view, setView] = useBuyerSupplierView();
  const { supplierMentionType, range } = view;
  const dateFrom = rangeStart(range);
  const onFit = useCallback((fitted: BuyerSupplierRange) => setView({ range: fitted }), [setView]);
  const { isFitting } = useFittedRange({
    buyerGuid,
    supplierGuid,
    hasSpendAccess,
    supplierMentionType,
    onFit,
  });

  const spendFilters = { ...supplierSpendFilter({ buyerGuid, supplierGuid }), dateFrom };

  const noticeFilters: NoticeFilters = {
    ...EMPTY_FILTERS,
    buyers: [buyerGuid],
    supplierGuids: [supplierGuid],
    supplierMentionType,
    // The same absolute start date as the figures and chart, so all three cut off on the same day.
    publishDate: dateFrom ? { from: dateFrom } : {},
  };

  if (isFitting) {
    // Held until the range is known, so the page doesn't load one period and then jump.
    return <RelationshipPlaceholder breadcrumbOverrides={breadcrumbOverrides} />;
  }

  return (
    <RelationshipOverview
      buyerGuid={buyerGuid}
      supplierGuid={supplierGuid}
      buyerName={buyerName}
      supplierName={supplierName}
      breadcrumbOverrides={breadcrumbOverrides}
      header={header}
      hasSpendAccess={hasSpendAccess}
      view={view}
      onViewChange={setView}
      spendFilters={spendFilters}
      noticeFilters={noticeFilters}
    />
  );
}

/** The page's frame with one placeholder where the overview will be. */
function RelationshipPlaceholder({
  breadcrumbOverrides,
}: Pick<BuyerSupplierRelationshipContentProps, "breadcrumbOverrides">) {
  return (
    <>
      <BreadcrumbContainer>
        <RouterBreadcrumb overrides={breadcrumbOverrides} />
      </BreadcrumbContainer>
      <DetailsPage>
        <Overview>
          <BuyerSupplierOverviewSkeleton />
        </Overview>
      </DetailsPage>
    </>
  );
}

type RelationshipOverviewProps = BuyerSupplierRelationshipContentProps & {
  hasSpendAccess: boolean;
  view: BuyerSupplierView;
  onViewChange: (update: Partial<BuyerSupplierView>) => void;
  spendFilters: SpendFilters;
  noticeFilters: NoticeFilters;
};

function RelationshipOverview({
  buyerGuid,
  supplierGuid,
  buyerName,
  supplierName,
  breadcrumbOverrides,
  header,
  hasSpendAccess,
  view,
  onViewChange,
  spendFilters,
  noticeFilters,
}: RelationshipOverviewProps) {
  const { supplierMentionType, interval, range } = view;
  const dateFrom = rangeStart(range);

  // The placeholder stays up until the figures and chart are both in, then the page appears in
  // one go. After that, changing the range reloads each piece in place.
  const figuresLoading = useHeadlineFiguresLoading({
    buyerGuid,
    supplierGuid,
    hasSpendAccess,
    supplierMentionType,
    dateFrom,
  });
  // The chart's own queries, so nothing is fetched twice.
  const { isLoading: chartLoading } = useBuyerSupplierChartRows({
    buyerGuid,
    supplierGuid,
    hasSpendAccess,
    supplierMentionType,
    interval,
    dateFrom,
  });
  const [hasShown, setHasShown] = useState(false);
  const isReady = hasShown || (!figuresLoading && !chartLoading);
  useEffect(() => {
    if (isReady) {
      setHasShown(true);
    }
  }, [isReady]);

  if (!isReady) {
    return <RelationshipPlaceholder breadcrumbOverrides={breadcrumbOverrides} />;
  }

  const tabs: TabsProps["items"] = [
    {
      key: "notices",
      label: "Notices",
      children: (
        <FilterableNoticeTable
          defaultFilters={noticeFilters}
          simple
          hiddenSections={["buyers", "suppliers"]}
          defaultColumns={["title", "expiry_date", "value", "published_date", "relevance_score"]}
        />
      ),
    },
    // The spend half of this page is paywalled; the notices half is not, so the
    // tab drops out and the chart falls back to notices, rather than losing the page.
    ...(hasSpendAccess
      ? [
          {
            key: "transactions",
            label: "All transactions",
            children: <BuyerSupplierTransactions filters={spendFilters} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <BreadcrumbContainer>
        <RouterBreadcrumb overrides={breadcrumbOverrides} />
      </BreadcrumbContainer>
      <DetailsPage>
        <Overview>
          <Text h1>{header}</Text>
          <BuyerSupplierViewControls view={view} onChange={onViewChange} />
          <BuyerSupplierHeadlineFigures
            buyerGuid={buyerGuid}
            supplierGuid={supplierGuid}
            hasSpendAccess={hasSpendAccess}
            supplierMentionType={supplierMentionType}
            dateFrom={dateFrom}
          />
          {/* No visible heading, per the design; the label keeps the chart findable by screen readers. */}
          <section
            aria-label={
              hasSpendAccess ? "Invoiced spend vs award value over time" : "Award value over time"
            }
          >
            <BuyerSupplierChart
              supplierGuid={supplierGuid}
              buyerGuid={buyerGuid}
              buyerName={buyerName}
              supplierName={supplierName}
              hasSpendAccess={hasSpendAccess}
              supplierMentionType={supplierMentionType}
              interval={interval}
              dateFrom={dateFrom}
            />
          </section>
        </Overview>
        <TabsSection>
          <Tabs items={tabs} />
        </TabsSection>
      </DetailsPage>
    </>
  );
}

// The summary above, the detail below. The tab bar's own full-width rule splits them, and the tab
// bar and its panel each take the page gutter.
const Overview = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 32,
  padding: 24,
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const TabsSection = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundDefault,
  // antd5's classes carry the ant5- prefix in this app.
  "& .ant5-tabs-nav": {
    margin: 0,
    padding: "0 24px",
  },
  "& .ant5-tabs-content-holder": {
    padding: 24,
  },
}));

export default SupplierBuyerRelationshipPage;
